import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { setDocumentTitle } from '../../utils/documentTitle';
import ProductCard from './ProductCard';
import ProductListCard from './ProductListCard';
import Pagination from './Pagination';
import BottomNavigation from '../Layout/BottomNavigation';
import { useAuth } from '../../context/AuthContext';
import { useMarketplace } from '../../context/MarketplaceContext';
import { useReview } from '../../context/ReviewContext';

const ProductsList = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { addToCart } = useMarketplace();
  const { getProductReviewStats, getStoreReviewStats } = useReview();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest'); // newest, price_low, price_high, rating, popularity
  const [viewMode, setViewMode] = useState('grid'); // grid, list, compact
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  // Adjust itemsPerPage based on view mode
  useEffect(() => {
    if (viewMode === 'grid') {
      setItemsPerPage(12); // 12 cards per page for grid
    } else if (viewMode === 'list') {
      setItemsPerPage(10); // 10 cards per page for list (more space per card)
    } else if (viewMode === 'compact') {
      setItemsPerPage(20); // 20 cards per page for compact (smaller cards)
    }
    setCurrentPage(1); // Reset to first page when changing view
  }, [viewMode]);
  const [userStoreId, setUserStoreId] = useState(null);

  // Categories
  const categories = [
    { id: 'all', name: t('marketplace.all'), icon: '📦' },
    { id: 'Flowers', name: t('marketplace.flowers'), icon: '🌿' },
    { id: 'Seeds', name: t('marketplace.seeds'), icon: '🌱' },
    { id: 'Edibles', name: t('marketplace.edibles'), icon: '🍪' },
    { id: 'Pre Rolls', name: t('marketplace.preRolls'), icon: '🚬' },
    { id: 'Cannabis Oils', name: t('marketplace.cannabisOils'), icon: '💧' },
    { id: 'CBD Products', name: t('marketplace.cbdProducts'), icon: '🌿' }
  ];

  // Sort options
  const sortOptions = [
    { value: 'newest', label: 'ใหม่ล่าสุด', icon: '🆕' },
    { value: 'price_low', label: 'ราคาต่ำ-สูง', icon: '💰' },
    { value: 'price_high', label: 'ราคาสูง-ต่ำ', icon: '💎' },
    { value: 'rating', label: 'คะแนนสูงสุด', icon: '⭐' },
    { value: 'popularity', label: 'ยอดนิยม', icon: '🔥' }
  ];

  // View modes
  const viewModes = [
    { value: 'grid', label: 'Grid', icon: '⊞' },
    { value: 'list', label: 'List', icon: '☰' },
    { value: 'compact', label: 'Compact', icon: '☷' }
  ];

  useEffect(() => {
    setDocumentTitle('สินค้าทั้งหมด - CannaTrade');
    fetchUserStoreId();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory]); // Re-fetch when category changes

  useEffect(() => {
    applyFilters();
  }, [products, searchTerm, selectedCategory, sortBy]);

  const fetchUserStoreId = async () => {
    if (!currentUser) return;
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        setUserStoreId(userData?.storeId || null);
      }
    } catch (error) {
      console.error('Error fetching user store ID:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      let productsQuery;
      
      // Query based on category
      if (selectedCategory !== 'all') {
        try {
          productsQuery = query(
            collection(db, 'products'),
            where('status', '==', 'active'),
            where('category', '==', selectedCategory),
            limit(200)
          );
        } catch (error) {
          productsQuery = query(
            collection(db, 'products'),
            where('status', '==', 'active'),
            limit(200)
          );
        }
      } else {
        productsQuery = query(
          collection(db, 'products'),
          where('status', '==', 'active'),
          limit(200)
        );
      }

      const snapshot = await getDocs(productsQuery);
      let productsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Don't sort here - let applyFilters handle it
      setProducts(productsData);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...products];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(product => 
        product.name?.toLowerCase().includes(searchLower) ||
        product.description?.toLowerCase().includes(searchLower) ||
        product.category?.toLowerCase().includes(searchLower)
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }

    // Sort
    if (sortBy === 'newest') {
      filtered.sort((a, b) => {
        const aDate = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const bDate = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return bDate.getTime() - aDate.getTime();
      });
    } else if (sortBy === 'price_low') {
      filtered.sort((a, b) => (a.price || 0) - (b.price || 0));
    } else if (sortBy === 'price_high') {
      filtered.sort((a, b) => (b.price || 0) - (a.price || 0));
    } else if (sortBy === 'rating') {
      // Sort by rating (requires review data - simplified for now)
      filtered.sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0));
    } else if (sortBy === 'popularity') {
      // Sort by popularity (views, sales, etc.)
      filtered.sort((a, b) => {
        const aPopularity = (a.views || 0) + (a.sales || 0) * 10;
        const bPopularity = (b.views || 0) + (b.sales || 0) * 10;
        return bPopularity - aPopularity;
      });
    }

    setFilteredProducts(filtered);
    setCurrentPage(1);
  };

  const handleAddToCart = async (product) => {
    if (!currentUser) {
      alert('กรุณาเข้าสู่ระบบก่อนเพิ่มสินค้าลงตะกร้า');
      return;
    }
    try {
      await addToCart(product);
      alert('เพิ่มสินค้าลงตะกร้าเรียบร้อยแล้ว');
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('เกิดข้อผิดพลาดในการเพิ่มสินค้าลงตะกร้า');
    }
  };

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">กำลังโหลดสินค้า...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-4 md:py-6">
        {/* Header */}
        <div className="mb-4 md:mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">📦 สินค้าทั้งหมด</h1>
          <p className="text-sm md:text-base text-gray-600">
            ค้นหาและเลือกซื้อสินค้าคุณภาพ
          </p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-4 md:mb-6">
          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                placeholder="ค้นหาสินค้า..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 md:py-3 pl-10 md:pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <svg
                className="absolute left-3 md:left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Category Filter */}
          <div className="mb-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>{category.icon}</span>
                  <span className="text-sm md:text-base">{category.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Sort and View Mode */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Sort Options */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm md:text-base text-gray-600">เรียงตาม:</span>
              <div className="flex items-center gap-2 flex-wrap">
                {sortOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => setSortBy(option.value)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                      sortBy === option.value
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span>{option.icon}</span>
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-2">
              <span className="text-sm md:text-base text-gray-600">มุมมอง:</span>
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                {viewModes.map(mode => (
                  <button
                    key={mode.value}
                    onClick={() => setViewMode(mode.value)}
                    className={`px-3 py-1.5 rounded transition-colors ${
                      viewMode === mode.value
                        ? 'bg-green-500 text-white'
                        : 'text-gray-700 hover:bg-gray-200'
                    }`}
                    title={mode.label}
                  >
                    <span className="text-base">{mode.icon}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4 text-sm md:text-base text-gray-600">
          พบ {filteredProducts.length} สินค้า
        </div>

        {/* Products Display */}
        {paginatedProducts.length > 0 ? (
          <>
            {/* Grid View */}
            {viewMode === 'grid' && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 md:gap-3 mb-6">
                {paginatedProducts.map(product => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onAddToCart={handleAddToCart}
                    userStoreId={userStoreId}
                  />
                ))}
              </div>
            )}

            {/* List View */}
            {viewMode === 'list' && (
              <div className="space-y-3 mb-6">
                {paginatedProducts.map(product => (
                  <ProductListCard
                    key={product.id}
                    product={product}
                    onAddToCart={handleAddToCart}
                    userStoreId={userStoreId}
                  />
                ))}
              </div>
            )}

            {/* Compact View */}
            {viewMode === 'compact' && (
              <div className="space-y-2 mb-6">
                {paginatedProducts.map(product => (
                  <div
                    key={product.id}
                    className="bg-white rounded-lg shadow-sm p-2 md:p-3 hover:shadow-md transition-shadow"
                  >
                    <Link to={`/marketplace/products/${product.id}`} className="flex items-center gap-3">
                      {/* Image */}
                      <div className="w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-green-400 to-green-600">
                        {product.images && product.images.length > 0 ? (
                          <img
                            src={product.images[0]}
                            alt={product.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = '<img src="/logo.png" alt="Logo" class="w-full h-full object-contain opacity-80" onerror="this.style.display=\'none\'; this.parentElement.innerHTML=\'<div class=\'text-white text-xl\'>🌿</div>\';" />';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <img 
                              src="/logo.png" 
                              alt="CannaTrade Logo" 
                              className="w-10 h-10 object-contain opacity-80"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = '<div class="text-white text-xl">🌿</div>';
                              }}
                            />
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm md:text-base font-medium text-gray-800 truncate">
                          {product.name}
                        </h3>
                        <p className="text-xs md:text-sm text-gray-500 truncate">
                          {product.storeName || 'ร้านค้า'}
                        </p>
                        <p className="text-sm md:text-base font-bold text-green-600 mt-1">
                          ฿{product.price?.toLocaleString()}
                        </p>
                      </div>

                      {/* Add to Cart Button */}
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          handleAddToCart(product);
                        }}
                        className="bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors flex-shrink-0"
                      >
                        เพิ่ม
                      </button>
                    </Link>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 mb-20 md:mb-6">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredProducts.length}
                  onItemsPerPageChange={(newItemsPerPage) => {
                    setItemsPerPage(newItemsPerPage);
                    setCurrentPage(1);
                  }}
                />
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-8 md:p-12 text-center mb-20 md:mb-6">
            <div className="text-6xl mb-4">📦</div>
            <p className="text-lg md:text-xl text-gray-800 mb-2">ไม่พบสินค้า</p>
            <p className="text-sm md:text-base text-gray-600">
              ลองเปลี่ยนคำค้นหาหรือตัวกรองดูนะครับ
            </p>
          </div>
        )}
      </div>

      {/* Bottom Navigation - Mobile Only */}
      <BottomNavigation />
    </div>
  );
};

export default ProductsList;

